import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, PhotoIcon, PlayIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';

export default function MediaGallery({ messages, isOpen, onClose, conversation }) {
  const [allMedia, setAllMedia] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showViewer, setShowViewer] = useState(false);
  const [loading, setLoading] = useState(true);
  const galleryRef = useRef(null);
  const viewerRef = useRef(null);

  // Extract all media from messages
  useEffect(() => {
    if (!messages || messages.length === 0) {
      setAllMedia([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const extractedMedia = [];

    messages.forEach(message => {
      let mediaUrls = [];
      
      // Check for different possible field names (same logic as MessagePanel)
      if (message?.mediaUrls && Array.isArray(message.mediaUrls)) {
        mediaUrls = message.mediaUrls;
      } else if (message?.media_urls && Array.isArray(message.media_urls)) {
        mediaUrls = message.media_urls;
      } else if (message?.metadata?.raw?.payload?.media && Array.isArray(message.metadata.raw.payload.media)) {
        mediaUrls = message.metadata.raw.payload.media.map(media => media.url || media).filter(url => url);
      } else if (message?.metadata?.mediaUrls && Array.isArray(message.metadata.mediaUrls)) {
        mediaUrls = message.metadata.mediaUrls;
      }

      mediaUrls.forEach((url, index) => {
        if (url && typeof url === 'string') {
          // Determine media type from URL or file extension
          const isImage = url.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i) || 
                         url.includes('image') || 
                         (url.includes('firebasestorage.googleapis.com') && !url.includes('video'));
          const isVideo = url.match(/\.(mp4|mov|avi|webm|quicktime)(\?|$)/i) || 
                         url.includes('video') ||
                         (url.includes('firebasestorage.googleapis.com') && url.includes('video'));

          if (isImage || isVideo) {
            extractedMedia.push({
              id: `${message.id}-${index}`,
              url,
              type: isImage ? 'image' : 'video',
              timestamp: message.timestamp || message.received_at,
              direction: message.direction,
              messageId: message.id,
              text: message.text || message.body
            });
          }
        }
      });
    });

    // Sort by timestamp (newest first)
    extractedMedia.sort((a, b) => {
      const getTime = (timestamp) => {
        if (!timestamp) return 0;
        if (timestamp.seconds) return timestamp.seconds * 1000;
        if (typeof timestamp === 'string') return new Date(timestamp).getTime();
        if (timestamp instanceof Date) return timestamp.getTime();
        return 0;
      };
      return getTime(b.timestamp) - getTime(a.timestamp);
    });

    setAllMedia(extractedMedia);
    setLoading(false);
  }, [messages]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!showViewer) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          setShowViewer(false);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : allMedia.length - 1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          setSelectedIndex(prev => prev < allMedia.length - 1 ? prev + 1 : 0);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showViewer, allMedia.length]);

  // Handle clicking outside viewer to close
  useEffect(() => {
    if (!showViewer) return;

    const handleClickOutside = (e) => {
      if (viewerRef.current && !viewerRef.current.contains(e.target)) {
        setShowViewer(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showViewer]);

  const formatTimestamp = (timestamp) => {
    try {
      if (!timestamp) return '';
      
      let date;
      if (timestamp instanceof Date) {
        date = timestamp;
      } else if (timestamp?.toDate) {
        date = timestamp.toDate();
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (timestamp?.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        return '';
      }
      
      if (isNaN(date.getTime())) return '';
      
      const now = new Date();
      if (date.toDateString() === now.toDateString()) {
        return format(date, 'h:mm a');
      }
      
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      if (date > oneWeekAgo) {
        return format(date, 'EEE h:mm a');
      }
      
      return format(date, 'MM/dd/yyyy');
    } catch (error) {
      return '';
    }
  };

  const openViewer = (index) => {
    setSelectedIndex(index);
    setShowViewer(true);
  };

  const nextMedia = () => {
    setSelectedIndex(prev => prev < allMedia.length - 1 ? prev + 1 : 0);
  };

  const previousMedia = () => {
    setSelectedIndex(prev => prev > 0 ? prev - 1 : allMedia.length - 1);
  };

  const selectedMedia = allMedia[selectedIndex];

  if (!isOpen) return null;

  return (
    <>
      {/* Gallery Modal */}
      <div className="fixed inset-0 z-[60] overflow-hidden" style={{ position: 'fixed', zIndex: 8999 }}>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />
        
        <div className="fixed inset-4 sm:inset-8 flex items-center justify-center">
          <div 
            ref={galleryRef}
            className="w-full h-full max-w-6xl backdrop-blur-2xl border border-white/10 rounded-3xl overflow-hidden"
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 60px rgba(0,0,0,0.4)'
            }}
          >
            {/* Header */}
            <div className="p-6 border-b border-white/10 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-white">Media Gallery</h2>
                  <p className="text-sm text-white/60 mt-1">
                    {conversation?.lastContactNumber ? 
                      `Conversation with ${conversation.lastContactNumber}` : 
                      'Conversation Media'}
                    {allMedia.length > 0 && (
                      <span className="ml-2">• {allMedia.length} {allMedia.length === 1 ? 'item' : 'items'}</span>
                    )}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="w-10 h-10 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all duration-200"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 p-6 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-400"></div>
                </div>
              ) : allMedia.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-white/60">
                  <PhotoIcon className="w-16 h-16 mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Media Found</h3>
                  <p className="text-sm">This conversation doesn't contain any photos or videos yet.</p>
                </div>
              ) : (
                <div className="h-full">
                  {/* Media Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 h-full overflow-y-auto pr-2">
                    {allMedia.map((media, index) => (
                      <div
                        key={media.id}
                        className="group relative aspect-square cursor-pointer"
                        onClick={() => openViewer(index)}
                      >
                        <div 
                          className="w-full h-full rounded-xl overflow-hidden backdrop-blur-xl border border-white/10 hover:border-white/20 transition-all duration-200"
                          style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                          }}
                        >
                          {media.type === 'image' ? (
                            <img
                              src={media.url}
                              alt="Media"
                              className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : (
                            <div className="relative w-full h-full">
                              <video
                                src={media.url}
                                className="w-full h-full object-cover"
                                preload="metadata"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentNode.querySelector('.video-placeholder').style.display = 'flex';
                                }}
                              />
                              <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors">
                                <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                  <PlayIcon className="w-4 h-4 text-white ml-0.5" />
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Fallback placeholder */}
                          <div className="hidden w-full h-full bg-white/5 items-center justify-center video-placeholder">
                            <PhotoIcon className="w-8 h-8 text-white/40" />
                          </div>
                        </div>

                        {/* Metadata overlay */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity rounded-b-xl">
                          <div className="text-xs text-white">
                            <div className="flex items-center justify-between">
                              <span className={`w-2 h-2 rounded-full ${media.direction === 'outbound' ? 'bg-indigo-400' : 'bg-green-400'}`} />
                              <span>{formatTimestamp(media.timestamp)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full-screen Media Viewer */}
      {showViewer && selectedMedia && (
        <div className="fixed inset-0 z-[70] overflow-hidden" style={{ position: 'fixed', zIndex: 9999 }}>
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md" />
          
          <div className="fixed inset-0 flex items-center justify-center p-4" style={{ position: 'fixed', zIndex: 10000 }}>
            <div 
              ref={viewerRef}
              className="relative w-full h-full max-w-5xl max-h-full flex flex-col"
            >
              {/* Viewer Header */}
              <div className="flex items-center justify-between p-4 backdrop-blur-xl bg-black/30 rounded-t-2xl border border-white/10">
                <div className="flex items-center space-x-4">
                  <div className={`w-3 h-3 rounded-full ${selectedMedia.direction === 'outbound' ? 'bg-indigo-400' : 'bg-green-400'}`} />
                  <div className="text-white">
                    <div className="font-medium">{selectedMedia.type === 'image' ? 'Photo' : 'Video'}</div>
                    <div className="text-sm text-white/60">{formatTimestamp(selectedMedia.timestamp)}</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className="text-white/60 text-sm">
                    {selectedIndex + 1} of {allMedia.length}
                  </span>
                  <button
                    onClick={() => window.open(selectedMedia.url, '_blank')}
                    className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-all duration-200"
                    title="Open in new tab"
                  >
                    <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowViewer(false)}
                    className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-all duration-200"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Media Content */}
              <div className="flex-1 relative bg-black/20 backdrop-blur-xl border-x border-white/10 flex items-center justify-center overflow-hidden">
                {selectedMedia.type === 'image' ? (
                  <img
                    src={selectedMedia.url}
                    alt="Full size media"
                    className="max-w-full max-h-full object-contain"
                    style={{ minHeight: '200px' }}
                  />
                ) : (
                  <video
                    src={selectedMedia.url}
                    controls
                    autoPlay
                    className="max-w-full max-h-full object-contain"
                    style={{ minHeight: '200px' }}
                  >
                    Your browser does not support the video tag.
                  </video>
                )}

                {/* Navigation Arrows */}
                {allMedia.length > 1 && (
                  <>
                    <button
                      onClick={previousMedia}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white hover:bg-black/70 transition-all duration-200"
                    >
                      <ChevronLeftIcon className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextMedia}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white hover:bg-black/70 transition-all duration-200"
                    >
                      <ChevronRightIcon className="w-6 h-6" />
                    </button>
                  </>
                )}
              </div>

              {/* Message Context */}
              {selectedMedia.text && (
                <div className="p-4 backdrop-blur-xl bg-black/30 rounded-b-2xl border border-white/10">
                  <div className="text-white/80 text-sm">
                    <span className="text-white/60">Message: </span>
                    {selectedMedia.text}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
